import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/site/SiteNav";
import { Footer } from "@/components/site/Sections";
import {
  Mail,
  CheckCircle2,
  ArrowRight,
  ChevronRight,
  Sparkles,
  BarChart3,
  TrendingUp,
  Target,
  Users,
  Award,
  Globe,
  Clock,
  ShieldCheck,
  Check,
  Building2,
  FileCheck,
  Search,
  Zap,
  Layout,
  MessageSquare,
  Database,
  Lock,
  Send,
  MousePointerClick,
  UserCheck,
} from "lucide-react";

export const Route = createFileRoute("/services_/email-marketing")({
  head: () => ({
    meta: [
      {
        title:
          "Email Marketing Services — KMWAdvertising · Automation & CRM Nurturing",
      },
      {
        name: "description",
        content:
          "Email Marketing Services That Nurture Leads, Strengthen Customer Relationships and Increase Conversions. HubSpot, Mailchimp & Klaviyo Campaigns by KMWA LLP.",
      },
      {
        property: "og:title",
        content: "Email Marketing Services — KMWAdvertising",
      },
      {
        property: "og:description",
        content:
          "Build Meaningful Customer Connections with Strategic Email Marketing. High-ROI lifecycle automation, responsive HTML design, and segment targeting.",
      },
    ],
  }),
  component: EmailMarketingServicePage,
});

export function EmailMarketingServicePage() {
  const whyEmailMatters = [
    {
      title: "Direct Customer Engagement",
      desc: "Deliver valuable, personalized content straight to subscribers who have already demonstrated direct interest in your brand.",
      icon: Mail,
    },
    {
      title: "Lead Nurturing & Qualification",
      desc: "Guide prospects through sales funnels with automated lifecycle email sequences that educate and build purchase intent.",
      icon: Target,
    },
    {
      title: "Higher Customer Retention",
      desc: "Stay connected with existing buyers through newsletters, VIP loyalty offers, product updates, and re-engagement campaigns.",
      icon: UserCheck,
    },
    {
      title: "Consistent Website Traffic",
      desc: "Drive qualified subscriber traffic directly to new product launches, blog articles, landing pages, and special promotions.",
      icon: MousePointerClick,
    },
    {
      title: "High Measurable ROI",
      desc: "Track open rates, click-through rates, conversion attribution, and revenue per subscriber with precision.",
      icon: TrendingUp,
    },
  ];

  const emailServicesSuite = [
    {
      title: "Email Marketing Strategy",
      desc: "Audience research, customer journey mapping, email calendars, content planning, goal benchmarking, and list segmentation.",
      icon: Target,
    },
    {
      title: "Email Campaign Management",
      desc: "Promotional campaigns, product launches, seasonal offers, newsletters, corporate announcements, and educational series.",
      icon: Send,
    },
    {
      title: "Audience Segmentation & Personalization",
      desc: "Behavioral, geographic, lifecycle stage, purchase history, and website engagement targeting for hyper-relevant messaging.",
      icon: Users,
    },
    {
      title: "Responsive Email Design & Copywriting",
      desc: "HTML email templates, mobile-first responsive layouts, compelling copywriting, subject line optimization, and A/B testing.",
      icon: Layout,
    },
    {
      title: "Marketing Automation & Drip Workflows",
      desc: "Automated welcome series, abandoned cart recovery, post-purchase onboarding, win-back flows, and lead scoring.",
      icon: Zap,
    },
    {
      title: "Analytics & Deliverability Optimization",
      desc: "Monitoring open rates, CTR, bounce rates, spam score checks, IP warming, domain authentication (SPF/DKIM/DMARC), and revenue tracking.",
      icon: BarChart3,
    },
  ];

  const audienceSegmentationCriteria = [
    "Geographic location & time zone",
    "Customer lifecycle & funnel stage",
    "Past purchase history & average order value (AOV)",
    "Website browsing & category activity",
    "Industry sector & company size (B2B)",
    "Engagement level (active vs dormant)",
    "Product category preference",
    "Lead magnet download source",
  ];

  const supportedPlatforms = [
    "HubSpot Marketing Hub",
    "Mailchimp Enterprise",
    "Klaviyo eCommerce Automation",
    "Brevo (formerly Sendinblue)",
    "ActiveCampaign",
    "Zoho Campaigns",
    "Salesforce Marketing Cloud",
    "Microsoft Dynamics 365",
    "Custom API & CRM Integrations",
  ];

  const analyticsMetrics = [
    "Unique & Total Open Rate (%)",
    "Click-Through Rate (CTR)",
    "Click-to-Open Rate (CTOR)",
    "Conversion & Revenue Attribution",
    "Hard & Soft Bounce Rate",
    "Unsubscribe & Spam Complaint Rate",
    "Email Deliverability & Inbox Placement",
    "List Growth & Churn Metrics",
  ];

  const kmwaStrengths = [
    "25+ years of strategic advertising & customer engagement heritage",
    "Certified email marketing strategists & copywriters",
    "Marketing automation & CRM integration specialists",
    "Behavioral audience segmentation playbooks",
    "Mobile-first responsive HTML email design",
    "Conversion-focused copy engineering & subject line A/B testing",
    "100% GDPR, DPDP & CAN-SPAM privacy compliance",
    "Domain authentication (SPF, DKIM, DMARC) & inbox placement focus",
    "Full integration with paid media, SEO, and web funnels",
    "High-ROI customer retention methodology",
  ];

  return (
    <div className="dark relative min-h-screen bg-background text-foreground flex flex-col w-full overflow-x-clip">
      <SiteNav />

      <main className="flex-1 w-full overflow-x-clip pt-24 md:pt-28">
        {/* HERO SECTION */}
        <section className="relative overflow-hidden py-14 sm:py-20 md:py-24 border-b border-white/10">
          <div className="absolute inset-0 grid-bg opacity-15" aria-hidden />
          <div className="glow-orb -left-32 top-10 h-[450px] w-[450px] bg-[var(--glow-purple)] opacity-25" />
          <div className="glow-orb right-[-100px] top-20 h-[400px] w-[400px] bg-[var(--glow-cyan)] opacity-25" />

          <div className="container-x relative z-10">
            {/* Breadcrumb */}
            <div className="flex flex-wrap items-center gap-2.5 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">
              <Link to="/" className="hover:text-white transition">
                Home
              </Link>
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              <Link to="/services" className="hover:text-white transition">
                Services
              </Link>
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              <span className="text-[var(--glow-purple)] font-semibold">Email Marketing</span>
            </div>

            <div className="max-w-4xl">
              <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-muted-foreground border border-white/10 mb-6">
                <Mail className="h-3.5 w-3.5 text-[var(--glow-purple)]" />
                Digital Media Capabilities
              </div>

              <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.12] tracking-tight text-white">
                Email Marketing Services That Nurture Leads, Strengthen Customer Relationships and Increase Conversions
              </h1>

              <p className="mt-4 text-base font-mono text-white/80 uppercase tracking-widest">
                Email Marketing, Marketing Automation & CRM Nurturing
              </p>

              <p className="mt-4 text-lg sm:text-xl font-accent italic text-[var(--glow-purple)] font-medium border-l-2 border-[var(--glow-purple)] pl-4 py-1">
                Build Meaningful Customer Connections with Strategic Email Marketing
              </p>

              <div className="mt-6 space-y-4 text-base sm:text-lg text-white/90 font-normal leading-relaxed">
                <p>
                  Email remains one of the most effective and measurable digital marketing channels. While new platforms emerge, email marketing consistently delivers strong engagement, customer retention and high return on investment when executed with precision. It enables direct communication through personalized, relevant messaging.
                </p>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  Whether welcoming new subscribers, nurturing lead pipelines, launching products, or driving repeat purchases, email delivers the right message to the right subscriber at the exact moment of intent.
                </p>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  At <strong className="text-white">KMWAdvertising LLP</strong>, we develop data-driven email marketing strategies combining compelling design, copywriting, automation workflows, audience segmentation, and deliverability optimization.
                </p>
              </div>

              <div className="mt-8 flex flex-wrap gap-4">
                <a href="/contact-us" className="btn-hero !py-3.5 !px-7">
                  Request Email Campaign Strategy
                  <ArrowRight className="h-4 w-4" />
                </a>
                <Link to="/services" className="btn-ghost !py-3.5 !px-7">
                  Explore All Services
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* WHY EMAIL MARKETING MATTERS */}
        <section className="py-14 sm:py-20 border-b border-white/10 bg-white/[0.01]">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block mb-2">
                High-ROI Direct Channel
              </span>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-white">
                Why Email Marketing Matters
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mt-3 leading-relaxed">
                Email enables direct communication with audiences who have already expressed active interest in your products or services.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {whyEmailMatters.map((item, idx) => {
                const EIcon = item.icon;
                return (
                  <div
                    key={idx}
                    className="p-6 sm:p-8 rounded-2xl glass-strong border border-white/10 hover:border-white/20 transition duration-300 flex flex-col justify-between group"
                  >
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)]">
                          PILLAR 0{idx + 1}
                        </span>
                        <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white">
                          <EIcon className="h-5 w-5 text-[var(--glow-purple)]" />
                        </div>
                      </div>
                      <h3 className="font-display text-xl font-bold text-white mb-2">
                        {item.title}
                      </h3>
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* OUR EMAIL SERVICES & AUTOMATION SUITE */}
        <section className="py-16 md:py-24 border-b border-white/10 relative">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block mb-2">
                End-to-End Email Management
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
                Our Email Marketing Services
              </h2>
            </div>

            {/* 6 SERVICES CARDS */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
              {emailServicesSuite.map((svc, idx) => {
                const SIcon = svc.icon;
                return (
                  <div key={idx} className="p-6 sm:p-8 rounded-2xl glass-strong border border-white/10 hover:border-white/20 transition duration-300 space-y-4">
                    <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-[var(--glow-purple)]">
                      <SIcon className="h-5 w-5" />
                    </div>
                    <h4 className="font-display text-xl font-bold text-white">{svc.title}</h4>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{svc.desc}</p>
                  </div>
                );
              })}
            </div>

            {/* SEGMENTATION & PLATFORMS */}
            <div className="grid lg:grid-cols-12 gap-8 items-start">
              {/* Segmentation Criteria */}
              <div className="lg:col-span-6 p-8 rounded-3xl glass-strong border border-white/10 space-y-4">
                <h4 className="font-display text-xl font-bold text-white flex items-center gap-2">
                  <Users className="h-5 w-5 text-[var(--glow-purple)]" />
                  Audience Segmentation Vectors
                </h4>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  Delivering higher open and click-through rates by segmenting email subscribers based on key behavioral data points:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2">
                  {audienceSegmentationCriteria.map((crit, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                      <Check className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                      <span>{crit}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Supported Platforms */}
              <div className="lg:col-span-6 p-8 rounded-3xl glass-strong border border-white/10 bg-[#06060c]/90 space-y-4">
                <h4 className="font-display text-xl font-bold text-white flex items-center gap-2">
                  <Database className="h-5 w-5 text-sky-400" />
                  Platforms & CRMs We Work With
                </h4>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  We integrate seamlessly with major marketing automation engines and enterprise CRM platforms:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2">
                  {supportedPlatforms.map((plat, i) => (
                    <div key={i} className="p-2.5 rounded-xl bg-white/[0.03] border border-white/5 text-xs text-white/90 font-medium">
                      {plat}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ANALYTICS & PRIVACY */}
        <section className="py-16 md:py-24 border-b border-white/10 relative bg-white/[0.01]">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block mb-2">
                Deliverability & Analytics
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
                Campaign Tracking & Privacy Compliance
              </h2>
            </div>

            <div className="grid lg:grid-cols-12 gap-8 items-start">
              {/* Analytics Metrics */}
              <div className="lg:col-span-6 space-y-4">
                <h4 className="font-display text-xl font-bold text-white flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-[var(--glow-purple)]" />
                  Performance Metrics Monitored
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2">
                  {analyticsMetrics.map((am, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                      <Check className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                      <span>{am}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Privacy & Deliverability */}
              <div className="lg:col-span-6 p-6 rounded-2xl glass-strong border border-white/10 space-y-3">
                <h4 className="font-display text-lg font-bold text-white flex items-center gap-2">
                  <Lock className="h-5 w-5 text-amber-400" />
                  Inbox Deliverability & GDPR Compliance
                </h4>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  We configure SPF, DKIM, DMARC, and custom tracking domains to maximize inbox placement while strictly complying with GDPR, DPDP, and anti-spam regulations.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* WHY CHOOSE KMWA LLP */}
        <section className="py-16 md:py-24 border-b border-white/10 relative">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block mb-2">
                25+ Years Engagement Heritage
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
                Why Choose KMWAdvertising LLP?
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {kmwaStrengths.map((str, i) => (
                <div key={i} className="p-4 rounded-xl bg-white/[0.03] border border-white/5 space-y-2">
                  <span className="font-mono text-xs text-[var(--glow-purple)] font-bold">0{i + 1}</span>
                  <p className="text-xs text-white/90 leading-snug font-medium">{str}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CLOSING CTA BANNER */}
        <section className="py-20 md:py-28 relative overflow-hidden">
          <div className="glow-orb left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[500px] bg-[var(--glow-purple)] opacity-20" />

          <div className="container-x relative z-10 text-center max-w-4xl">
            <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs uppercase tracking-widest text-muted-foreground border border-white/10 mb-6">
              <Mail className="h-3.5 w-3.5 text-[var(--glow-purple)]" />
              Email Marketing Advisory
            </div>

            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight">
              Ready to Build Meaningful Connections with Email Marketing?
            </h2>

            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Contact our email marketing specialists today for a tailored campaign strategy that combines compelling design, segmentation, and automated lead nurturing.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="/contact-us" className="btn-hero w-full sm:w-auto">
                Request Email Proposal
                <ArrowRight className="h-4 w-4" />
              </a>
              <Link to="/services" className="btn-ghost w-full sm:w-auto">
                Back to All Services
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
